/**
 * SoldAnnouncement.js
 *
 * Full-screen "SOLD!" flash shown on every screen in the room the moment the host sells an item.
 * Only announces new sales, not the one that was already there when the screen loaded.
 * The buyer and price are only shown if the host allows sales to be public.
 *
 * useSaleAnnouncement tracks the sale being announced, and keeps holding it until the
 * flash has fully faded out, so screens can keep showing the sold item until then.
 */

import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const SHOW_FOR_MS = 3500;

/**
 * @param {Object} lastSale     -   room.lastSale
 * @returns {Object}            -   { sale, heldSale, dismiss, release }
 *                                  sale     : the sale on screen right now (null when not showing)
 *                                  heldSale : the sale until its flash has fully faded out
 */
export const useSaleAnnouncement = (lastSale) => {
    const [sale, setSale] = useState(null);
    const [heldSale, setHeldSale] = useState(null);
    const seenSoldAt = useRef(lastSale?.soldAt ?? null);

    // Only react to a new sale (room snapshots arrive often, with new objects each time)
    const soldAt = lastSale?.soldAt;
    useEffect(() => {
        if (!soldAt || soldAt === seenSoldAt.current) return;
        seenSoldAt.current = soldAt;
        setSale(lastSale);
        setHeldSale(lastSale);
    }, [soldAt]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!sale) return;
        const timer = setTimeout(() => setSale(null), SHOW_FOR_MS);
        return () => clearTimeout(timer);
    }, [sale]);

    return {
        sale,
        heldSale,
        dismiss : () => setSale(null),
        release : () => setHeldSale(null),
    };
};

const SoldAnnouncement = ({ sale, showSales, onDismiss, onFaded }) => {
    return (
        <AnimatePresence onExitComplete={onFaded}>
            {sale && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onDismiss}
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm px-6 text-center"
                >
                    <motion.div
                        initial={{ scale: 2.5, rotate: -12, opacity: 0 }}
                        animate={{ scale: 1, rotate: -4, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 260, damping: 14 }}
                    >
                        <div className="inline-block rounded-3xl border-[6px] border-yellow px-10 py-4 text-yellow font-extrabold tracking-widest text-[clamp(4rem,14vw,10rem)] leading-none">
                            SOLD!
                        </div>
                        <div className="mt-8 text-white text-[clamp(1.75rem,4vw,3.5rem)] font-bold leading-tight">
                            {sale.name ?? sale.movieName}
                        </div>
                        {showSales && (
                            <div className="mt-3 text-zinc-300 text-[clamp(1.25rem,2.5vw,2.25rem)]">
                                to <span className="text-white font-semibold">{sale.teamName}</span> for{" "}
                                <span className="text-yellow font-semibold">₹{sale.amount}</span>
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default SoldAnnouncement;
